import { useEffect, useRef } from 'react'
import { useExperienceStore } from '../store/experienceStore'
import { extractFeatures } from '../core/gesture/featureExtract'
import { mapGestureToControls } from '../core/gesture/gestureMapper'
import {
  DEFAULT_FEATURE_CFG,
  DEFAULT_MAPPING_CFG,
  type HandLandmarks,
} from '../core/gesture/types'

const CDN_WASM =
  'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.32/wasm'
const CDN_MODEL =
  'https://storage.googleapis.com/mediapipe-models/hand_landmarker/hand_landmarker_lite/float16/1/hand_landmarker_lite.task'

/**
 * MediaPipe Hands → Mode B controls.
 * Uses CDN for WASM/model (works without vendored public/mediapipe).
 * Camera frames never leave the device; only model weights are fetched once.
 */
export function useMediaPipeHands(
  video: HTMLVideoElement | null,
  enabled: boolean,
) {
  const landmarkerRef = useRef<{
    detectForVideo: (
      video: HTMLVideoElement,
      ts: number,
    ) => {
      landmarks?: Array<Array<{ x: number; y: number; z: number }>>
      handednesses?: Array<Array<{ categoryName: string; score: number }>>
    }
    close?: () => void
  } | null>(null)
  const rafRef = useRef(0)
  const lastInferRef = useRef(0)
  const lastTsRef = useRef(0)

  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    const store = useExperienceStore.getState()
    store.setGestureModelStatus('loading')

    ;(async () => {
      try {
        const vision = await import('@mediapipe/tasks-vision')
        const { FilesetResolver, HandLandmarker } = vision
        const fileset = await FilesetResolver.forVisionTasks(CDN_WASM)
        if (cancelled) return
        const landmarker = await HandLandmarker.createFromOptions(fileset, {
          baseOptions: {
            modelAssetPath: CDN_MODEL,
            delegate: 'GPU',
          },
          runningMode: 'VIDEO',
          numHands: 2,
        })
        if (cancelled) {
          landmarker.close()
          return
        }
        landmarkerRef.current = landmarker
        useExperienceStore.getState().setGestureModelStatus('ready')
      } catch {
        // Retry CPU delegate
        try {
          const vision = await import('@mediapipe/tasks-vision')
          const { FilesetResolver, HandLandmarker } = vision
          const fileset = await FilesetResolver.forVisionTasks(CDN_WASM)
          if (cancelled) return
          const landmarker = await HandLandmarker.createFromOptions(fileset, {
            baseOptions: {
              modelAssetPath: CDN_MODEL,
              delegate: 'CPU',
            },
            runningMode: 'VIDEO',
            numHands: 2,
          })
          if (cancelled) {
            landmarker.close()
            return
          }
          landmarkerRef.current = landmarker
          useExperienceStore.getState().setGestureModelStatus('ready')
        } catch {
          useExperienceStore.getState().setGestureModelStatus('failed')
          useExperienceStore.getState().setInputMode('mouse')
          useExperienceStore
            .getState()
            .setBannerMessage('手势模型加载失败，已切换为键鼠控制')
        }
      }
    })()

    return () => {
      cancelled = true
      landmarkerRef.current?.close?.()
      landmarkerRef.current = null
    }
  }, [enabled])

  useEffect(() => {
    if (!enabled || !video) return

    const loop = () => {
      rafRef.current = requestAnimationFrame(loop)
      const landmarker = landmarkerRef.current
      if (!landmarker || video.readyState < 2) return

      const now = performance.now()
      // ≤30Hz inference
      if (now - lastInferRef.current < 33) return
      lastInferRef.current = now

      try {
        const result = landmarker.detectForVideo(video, now)
        const hands: HandLandmarks[] = (result.landmarks ?? []).map((lm) =>
          lm.map((p) => ({ x: p.x, y: p.y, z: p.z })),
        )
        const handedness = (result.handednesses ?? []).map((h) => {
          const name = h[0]?.categoryName
          return name === 'Left' || name === 'Right' ? name : 'Right'
        }) as Array<'Left' | 'Right'>
        const confidences = (result.handednesses ?? []).map(
          (h) => h[0]?.score ?? 0,
        )

        const dt =
          lastTsRef.current === 0
            ? 0.033
            : Math.min(0.1, (now - lastTsRef.current) / 1000)
        lastTsRef.current = now

        const features = extractFeatures(
          hands,
          handedness,
          confidences,
          DEFAULT_FEATURE_CFG,
        )
        const prevSpeed = useExperienceStore.getState().timeSpeed
        const controls = mapGestureToControls(
          features,
          DEFAULT_MAPPING_CFG,
          dt,
          prevSpeed,
        )

        // palmNdc from palm in mirrored space: x 0..1 → -1..1
        const palmNdc =
          hands.length > 0
            ? {
                x: (features.palmX - 0.5) * 2,
                y: -(features.palmY - 0.5) * 2,
              }
            : null

        if (hands.length === 0) {
          useExperienceStore.getState().setHandsVisible(false)
          // still decay speed via mapper
          useExperienceStore.getState().setGestureControls({
            ...controls,
            palmNdc: null,
          })
        } else {
          useExperienceStore.getState().setGestureControls({
            ...controls,
            palmNdc,
          })
        }
      } catch {
        // ignore intermittent detect errors
      }
    }

    rafRef.current = requestAnimationFrame(loop)
    return () => cancelAnimationFrame(rafRef.current)
  }, [video, enabled])
}
